<template>
    <li
        :class="[
            'combo-content',
            {
                'combo-content-selected': selected,
                'combo-disabled': planSeriesDisabled
            }
        ]"
        @click="togglePlanSeries"
    >
        <div
            :class="[
                'combo-head',
                {
                    'combo-disabled': planSeriesDisabled,
                    'selectable': isShowSelect || showComboExpand
                }
            ]"
        >
            <div class='plan-name' :class="{'is-multi-plan': showComboExpand}">
                <reo-checkbox
                    v-if="isShowSelect"
                    :disabled="planSeriesDisabled"
                    :isCanSelect="!showComboExpand"
                    :modelValue="selected"
                    @click="togglePlanSeries"
                    @change="handleComboChange"
                ></reo-checkbox>
                <span class="combo-title" :class="{'selectable': isShowSelect }">{{ seriesName }}</span>
            </div>

            <div class="price-tag-wrapper">
                <div class='price-wrapper'>
                    <!-- 多套餐 -->
                    <template v-if="showComboExpand">
                        <div>
                            <span class="price">{{ priceRange }}</span>
                        </div>
                        <span class="combo-expand">
                            <co-up :class="comboExpand ? 'spanReset' : 'spanRotate'" ></co-up>
                        </span>
                    </template>
                    <!-- 单套餐 -->
                    <template v-else>
                        <span class="origin-price" v-if="regularPrice">{{ regularPrice }}</span>
                        <span class="price">{{ priceRange }}</span>
                    </template>
                </div>
                <current-plan-tag v-if="!showComboExpand && currSubPlanId===singlePlan?.id && !currSubExpired"/>
            </div>
        </div>

        <div
            class="describe"
            :class="{'expandable': showComboExpand && comboExpand,'selectable': isShowSelect}">
            <template
                v-if="Object.keys(maxDescription).length>0"
            >
                <div v-for="(desc,key) of maxDescription" :key="key" class='describe-item'>
                    <span v-html="desc"></span>
                    <reo-tip v-if="key===ESupportFeatures.NOTICE">
                        {{ $t('tip_rich_notification') }}
                    </reo-tip>
                    <span>{{ comma }}</span>
                </div>
                <div class='describe-item' v-if="allowAI.support">
                    <span v-html="allowAI.name"></span>
                    <reo-tip>
                        {{allowAI.description}}
                    </reo-tip>
                </div>
            </template>
            <!-- 单套餐时显示套餐的备注，多套餐时显示系列备注 -->
            <rich-text class="note" v-if='showComboExpand && seriesNotice' :html="seriesNotice"></rich-text>
            <rich-text class="note" v-if='!showComboExpand && singlePlanDesc' :html="singlePlanDesc"></rich-text>
            <rich-text class="note" v-if='!showComboExpand && getPlanPromotionText(singlePlan)' :html="getPlanPromotionText(singlePlan)" />
        </div>

        <template v-if="showComboExpand && comboExpand">
            <ul class="combo-items-content">
                <li
                    v-for="(cycleMap, strategyKey) in planSeries"
                    :key="strategyKey"
                    class="combo-item"
                    :class="{'multi-plan': isMultiplePlans, 'combo-disabled': cycleMapDisabled(cycleMap)}">
                    <div
                        v-for="(plans, cycleKey) in cycleMap"
                        :key="cycleKey"
                        :class="{'multi-plan': isMultiplePlans, 'combo-disabled': plansDisabled(plans) }">
                        <div
                            v-for="plan of plans"
                            :key="plan.id"
                            :class="['combo-head',{'combo-disabled': planDisabled(plan), 'combo-single': isMultiplePlans && !isMultipleStrategy}]">
                            <div class="plan-name" :class="{'combo-disabled': planDisabled(plan)}" @click="handleComboChange(!plan.checked,plan.id)">
                                <reo-checkbox
                                    v-if="isShowSelect"
                                    :modelValue="plan.checked"
                                    :disabled="planDisabled(plan)"
                                    @change="(value) => handleComboChange(value, plan.id)"
                                ></reo-checkbox>
                                <span class="combo-title" :class="{selectable: isShowSelect,'combo-disabled': planDisabled(plan)}">
                                    {{ $tc( `cycle_count.${plan.duration.unit}`, plan.duration.amount, {
                                        count: plan.duration.amount
                                    }) }} {{ $t('plan_item_subscription') }}
                                </span>
                            </div>
                            <div class="price-tag-wrapper">
                                <div class='price-wrapper'>
                                    <span
                                        v-if="planOriginPrice(plan)"
                                        class="origin-price"
                                    >{{planOriginPrice(plan)}}</span>
                                    <span class="price">{{planPrice(plan)}}</span>
                                </div>
                                <current-plan-tag v-if="currSubPlanId === plan.id && !currSubExpired"/>
                            </div>
                            <div
                                v-if="plan.i18n.description"
                                :class="['note-container',{selectable: isShowSelect}]">
                                <rich-text class="note" :html="plan.i18n.description" />
                            </div>
                            <div
                                v-if="getPlanPromotionText(plan)"
                                :class="['note-container',{selectable: isShowSelect}]">
                                <rich-text class="note" v-if="getPlanPromotionText(plan)" :html="getPlanPromotionText(plan)" />
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="isMultipleStrategy && Object.keys(getStrategyDetails(strategyKey)).length>0"
                        class="describe"
                        :class="{'expandable': showComboExpand,'selectable': isShowSelect}">
                        <div v-for="(desc,key,index) of getStrategyDetails(strategyKey)" :key="key" class="describe-item">
                            <span v-html="desc"></span>
                            <span v-if="index!==Object.keys(getStrategyDetails(strategyKey)).length-1">{{ comma }}</span>
                        </div>
                    </div>
                </li>
            </ul>
        </template>
    </li>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoTip from '@/basic/Tip.vue';
import ReoCheckbox from '@/components/reoCheckbox.vue';
import CoUp from '@/components/Svg/CO_UP.vue';
import { ICycleMap, IStrategyMap, productsManager } from '@/lib';
import { EPlanStatus, ESeriesCategory, ESubscribeMode, IPlan, IPromotionItem } from '@reolink-cloud/interface';
import CurrentPlanTag from './CurrentPlanTag.vue';
import { config } from 'CONFIG';
import { getPlanDataSizeStr, getPlanPromotionText } from '@/utils/business';

enum ESupportFeatures {
    NOTICE = 'richNotification',
    HISTORY = 'history',
    DATA_SIZE = 'dataSize',
    AI = 'ai'
}

type descMap = Partial<Record<ESupportFeatures, string>>;

export default Vue.extend({
    components: {
        ReoCheckbox,
        ReoTip,
        CurrentPlanTag,
        CoUp
    },
    props: {
        isShowSelect: {
            type: Boolean,
            default: false
        },
        subscribeMode: {
            type: String as PropType<ESubscribeMode>,
            default: ESubscribeMode.CREATE
        },
        planSeries: {
            type: Object as PropType<IStrategyMap>
        },
        seriesKey: String,
        currSelectedPlanId: String,
        currSubPlanId: String,
        currSubExpired: Boolean,
        promotions: Array as PropType<IPromotionItem[]>

    },
    data() {

        const seriesInfo = productsManager.getPlanSeriesByKey( this.seriesKey );
        const isRecommend = seriesInfo.style['series.recommended'] === 1;

        return {
            config,
            ESupportFeatures,
            productsManager,
            seriesInfo,
            comboExpand: isRecommend,
            selected: false,
            selectedPlanId: '',
            comma: this.$t( 'comma' ) as string

        };

    },
    computed: {
        planSeriesDisabled(): boolean {

            let disabledArr: boolean[] = [];

            for ( let cycleMap of Object.values( this.planSeries )) {

                disabledArr.push( this.cycleMapDisabled( cycleMap ));

            }

            return disabledArr.every( item => item );

        },

        isMultipleStrategy(): boolean {

            return Object.keys( this.planSeries ).length > 1;

        },

        isMultiplePlans(): boolean {

            for ( let cycleMap of Object.values( this.planSeries )) {

                if ( Object.keys( cycleMap ).length > 1 ) {

                    return true;

                }
                for ( let plans of Object.values( cycleMap )) {

                    if ( plans.length > 1 ) {

                        return true;

                    }

                }

            }
            return false;

        },

        /** 是否为多套餐 */
        showComboExpand(): boolean {

            if ( this.isMultipleStrategy || this.isMultiplePlans ) {

                return true;

            }
            return false;

        },

        maxDescription(): descMap {

            let maxHistory = 0;
            let richNotification = false;
            let maxSimDataPlan: IPlan | null = null;
            let maxDeviceSupport = 0;

            for ( const plan of Object.values( this.planSeries ).flatMap( cycleMap => Object.values( cycleMap ).flat())) {

                const defaultRetentionDay = plan.style['cloud_storage.default_retention_day'] ?? 0;
                maxHistory = defaultRetentionDay > maxHistory ? defaultRetentionDay : maxHistory;
                maxDeviceSupport = plan.style['cloud_storage.max_device_quantity']! > maxDeviceSupport ? plan.style['cloud_storage.max_device_quantity']! : maxDeviceSupport;
                richNotification = plan.style['cloud_storage.allow_rich_notification'] ?? false;
                if ( plan.style['cloud_storage.data_size'] ) {

                    if ( !maxSimDataPlan || plan.style['cloud_storage.data_size']  > ( maxSimDataPlan.style['cloud_storage.data_size'] || 0 )) {

                        maxSimDataPlan = plan;

                    }

                }

            }

            const richNotificationStr = this.$t( 'plan.richNotification' ) as string;
            let maxHistoryStr = this.$t( 'plan.maxHistory', this.lang, { history: maxHistory }) as string;
            let maxGalleryHistoryStr = this.$t( 'plan.maxGalleryHistory', this.lang, { history: maxHistory }) as string;
            let maxSimDataStr = '';

            if ( !this.isMultiplePlans || !this.isMultipleStrategy ) {

                maxSimDataStr = maxSimDataPlan ? getPlanDataSizeStr( maxSimDataPlan, this.$i18n ) : '';
                maxHistoryStr = this.$t( 'plan.history', this.lang, { history: maxHistory }) as string ;
                maxGalleryHistoryStr = this.$t( 'plan.galleryHistory', this.lang, { history: maxHistory }) as string;

            }

            const result: descMap = {};
            switch ( this.seriesInfo.category ) {

                case ESeriesCategory.CLOUD_STORAGE_DATA_PLAN:
                case ESeriesCategory.CLOUD_STORAGE_PLAN:{

                    if ( this.seriesInfo.category === ESeriesCategory.CLOUD_STORAGE_PLAN ) {

                        result[ESupportFeatures.HISTORY] = maxHistoryStr;

                    } else {

                        result[ESupportFeatures.DATA_SIZE] = maxSimDataStr;
                        result[ESupportFeatures.HISTORY] = maxGalleryHistoryStr;

                    }
                    if ( richNotification ) {

                        result[ESupportFeatures.NOTICE] = richNotificationStr;

                    }
                    break;

                }
                case ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN:
                case ESeriesCategory.CLOUD_DATA_PLAN:
                default:

            }
            return result;

        },

        allowAI(): { support: boolean;name: string;description: string; } {

            let aiInfo = {
                support: false,
                name: '',
                description: ''
            };
            for ( let cycleMap of Object.values( this.planSeries )) {

                for ( let plans of Object.values( cycleMap )) {

                    for ( let plan of plans ) {

                        if ( plan.style['cloud_storage.allow_ai'] && plan.style['cloud_storage.ai_group_id'] ) {

                            const aiGroupInfo = productsManager.getAiGroupById( plan.style['cloud_storage.ai_group_id'] );
                            if ( aiGroupInfo ) {

                                aiInfo.support = true;
                                aiInfo.name = aiGroupInfo.i18n.name;
                                aiInfo.description = aiGroupInfo.i18n.description;

                            }
                            break;

                        }

                    }

                }

            }
            return aiInfo;

        },

        priceRange(): string {

            let min = Infinity;
            let max = 0;
            let currency = 'USD';
            for ( let cycleMap of Object.values( this.planSeries )) {

                for ( let plans of Object.values( cycleMap )) {

                    for ( let plan of plans ) {

                        const salePrice = Number( plan.pricing.salePrice );

                        min = salePrice < min ? salePrice : min;

                        max = salePrice > max ? salePrice : max;

                        currency = plan.pricing.currency;

                    }

                }

            }
            if ( !this.showComboExpand ) {

                if ( min === 0 ) {

                    return this.$t( 'plan.free' ) as string;

                }
                return `${currency} ${min.toFixed( 2 )}`;

            } else {

                if ( max === min ) {

                    if ( min === 0 ) {

                        return this.$t( 'plan.free' ) as string;

                    }
                    return `${currency} ${min.toFixed( 2 )}`;

                } else {

                    if ( min === 0 ) {

                        return `${this.$t( 'plan.free' ) as string} - ${currency} ${max.toFixed( 2 )}`;

                    }

                }
                return `${currency} ${min.toFixed( 2 )} - ${currency} ${max.toFixed( 2 )}`;

            }

        },

        regularPrice(): string {

            let regularPrice = 0;
            let salePrice = 0;
            let currency = 'USD';
            for ( let cycleMap of Object.values( this.planSeries )) {

                for ( let plans of Object.values( cycleMap )) {

                    for ( let plan of plans ) {

                        regularPrice = Number( plan.pricing.regularPrice );
                        salePrice = Number( plan.pricing.salePrice );
                        currency = plan.pricing.currency;

                    }

                }

            }
            if ( !this.showComboExpand ) {

                if ( salePrice === regularPrice ) {

                    return '';

                }
                return `${currency} ${regularPrice.toFixed( 2 )}`;

            }
            return '';

        },

        seriesName(): string {

            const name = this.productsManager.getPlanSeriesByKey( this.seriesKey ).i18n.name;
            if ( this.singlePlan ) {

                return `${name} (${this.$tc( `cycle.${this.singlePlan.duration.unit}`, this.singlePlan.duration.amount, {

                    count: this.singlePlan.duration.amount

                })})`;

            }

            return name;

        },

        seriesNotice(): string {

            return this.productsManager.getPlanSeriesByKey( this.seriesKey ).i18n.desc;

        },

        singlePlan(): IPlan | null {

            if ( !this.showComboExpand ) {

                for ( let cycleMap of Object.values( this.planSeries )) {

                    for ( let plans of Object.values( cycleMap )) {

                        for ( let plan of plans ) {

                            return plan;

                        }

                    }

                }

            }
            return null;

        },

        singlePlanDesc(): string {

            return this.singlePlan?.i18n.description ?? '';

        },

        lang(): string  {

            return this.$store.getters.lang;

        },

    },
    created() {

        this.checkSelectAll();

    },
    methods: {

        togglePlanSeries(): void {

            if ( this.showComboExpand ) {

                this.comboExpand = !this.comboExpand;
                this.$emit( 'toggleSeries' );

            } else {

                if ( this.isShowSelect ) {

                    this.handleComboChange( !this.selected );

                }

            }

        },

        changeSelected( isSelected: boolean, planId: string ) {

            /** 选中对应选项，取消选中其余选项 */
            for ( let cycleMap of Object.values( this.planSeries )) {

                for ( let plans of Object.values( cycleMap )) {

                    for ( let plan of plans ) {

                        if ( plan.id === planId ) {

                            plan.checked = isSelected;

                        } else {

                            plan.checked = false;

                        }

                    }

                }

            }

            this.checkSelectAll();

        },

        /** 检查整个列表，是否选中顶层checkbox */
        checkSelectAll() {

            let shouldSelected = false;
            let tempSelectedPlanId = '';
            for ( let cycleMap of Object.values( this.planSeries )) {

                for ( let plans of Object.values( cycleMap )) {

                    for ( let plan of plans ) {

                        if (
                            plan.checked && (
                                ( this.subscribeMode === ESubscribeMode.CREATE ) ||
                                ( this.subscribeMode === ESubscribeMode.SWITCH && plan.limitations.switchFromOther )
                            )
                        ) {

                            shouldSelected = true;
                            tempSelectedPlanId = plan.id;

                        }

                    }

                }

            }

            this.selected = shouldSelected;
            this.selectedPlanId = tempSelectedPlanId;

        },

        handleComboChange( value: boolean, planId?: string ): void {

            if ( !this.isShowSelect ) {

                return;

            }
            if ( planId ) {

                this.changeSelected( value, planId );

            } else {

                this.changeSelected( value, this.singlePlan!.id );

            }

        },

        getStrategyDetails( strategyKey: string ): descMap {

            if (
                this.seriesInfo.category === ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN ||
                this.seriesInfo.category === ESeriesCategory.CLOUD_DATA_PLAN
            ) {

                return {};

            }

            for ( let cycleMap of Object.values( this.planSeries )) {

                for ( let plans of Object.values( cycleMap )) {

                    for ( let plan of plans ) {

                        if ( plan.storageStrategy === strategyKey ) {

                            let result: descMap = {};
                            if ( this.seriesInfo.category === ESeriesCategory.CLOUD_STORAGE_DATA_PLAN ) {

                                result[ESupportFeatures.HISTORY] = this.$t( 'plan.galleryHistory', this.lang, { history: plan.style['cloud_storage.default_retention_day'] ?? plan.style['cloud_storage.video_lifetime'] }) as string;

                            } else {

                                result[ESupportFeatures.HISTORY] = this.$t( 'plan.history', this.lang, { history: plan.style['cloud_storage.default_retention_day'] ?? plan.style['cloud_storage.video_lifetime'] }) as string;

                            }

                            return result;

                        }

                    }

                }

            }

            return {};

        },

        planDisabled( plan: IPlan ): boolean {

            if ( plan.status === EPlanStatus.RENEW_ONLY ) {

                return true;

            }

            if ( this.subscribeMode === ESubscribeMode.CREATE ) {

                return false;

            }

            if ( this.subscribeMode === ESubscribeMode.SWITCH && Number( plan.pricing.salePrice ) === 0 ) {

                return true;

            }

            return !plan.limitations.switchFromOther;

        },

        plansDisabled( plans: IPlan[] ): boolean {

            return plans.every( plan => this.planDisabled( plan ));

        },

        cycleMapDisabled( cycleMap: ICycleMap ): boolean {

            let disabledArr: boolean[] = [];
            for ( let plans of Object.values( cycleMap )) {

                disabledArr.push( this.plansDisabled( plans ));

            }
            return disabledArr.every( item => item );

        },

        planOriginPrice( plan: IPlan ): string {

            if ( Number( plan.pricing.salePrice ) < Number( plan.pricing.regularPrice )) {

                return `${plan.pricing.currency} ${plan.pricing.regularPrice}`;

            } else {

                return '';

            }

        },
        planPrice( plan: IPlan ): string {

            return Number( plan.pricing.salePrice ) !== 0 ? `${plan.pricing.currency} ${plan.pricing.salePrice}` : this.$t( 'plan.free' ) as string;

        },
        getPlanPromotionText( plan?: IPlan |null ): string|undefined {

            if ( !plan ) {

                return;

            }

            return getPlanPromotionText( this.promotions, plan );

        }

    },
    watch: {
        selectedPlanId( planId: string ) {

            this.$emit( 'selected', this.selected, planId, this.seriesKey );

        },
        currSelectedPlanId( newPlanId: string ) {

            if ( newPlanId ) {

                if ( newPlanId === this.selectedPlanId ) {

                    return;

                }

                this.changeSelected( true, newPlanId );

            }

        }
    }
});

</script>

<style lang="less" scoped>
.combo-content{
    background-color: @series-card-background;
    padding: 24px;
    border-width: @series-card-border-width-mobile;
    border-style: solid;
    border-color: @fill_primary;
    border-radius: @border-radius-level-2;
    cursor: pointer;
    &:hover{
        background-color: @b_1;
        border-color: @b_6;
    }
    @media @snapeak_mobile {
        padding: 12px;
        border-width: @series-card-border-width-mobile;
    }
    &.combo-content-selected {
        border-color: @brand_light;
        background-color: @series-card-selected-background-color;
        border-width: @series-card-border-width;
    }
    &:nth-of-type(n+2) {
        margin-top: 14px;
    }
    .plan-name{
        display: flex;
        align-items: center;
        gap: 12px;
        &.is-multi-plan{
            margin-right: 20px;
        }
    }
    .describe {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        @media @snapeak_pc{
            &.selectable{
                padding-left: 30px;
            }
        }

        &.expandable {
            border-bottom: 1px solid @line;
            padding-bottom: 12px;
            margin-bottom: 24px;
        }
        .describe-item{
            display: flex;
            &:nth-last-child(2){
                z-index: 8;
            }
        }
        font-family: @font_02;
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 0em;
        color: @font_secondary;
        @media @snapeak_mobile {
            font-size: 12px;
            line-height: 15px;
        }
    }
    .note {
        font-family: @font_02;
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 0em;
        color: @warning;
        padding-top: 12px;
        width: 100%;
        @media @snapeak_mobile {
            font-size: 12px;
            line-height: 15px;
        }
    }
    .note-container{
        width: 100%;
        .note{
            padding-top: 0;
       }
       &.selectable{
            @media @snapeak_pc{
                padding-left: 30px;
            }
       }
    }
    .price-tag-wrapper{
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
        @media @snapeak_mobile {
            gap: 8px;
            width: 100%;
            // flex-direction: row-reverse;
            // justify-content: flex-end;
        }
    }
    .price-wrapper{
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
        @media @snapeak_mobile {
            gap: 8px;
            // width: 100%;
            flex-direction: row-reverse;
            justify-content: flex-end;
        }
        .origin-price {
            font-weight: 500;
            text-decoration: line-through;
            color: @font_tertiary;
            @media @snapeak_mobile {
                font-size: 14px;
                font-weight: @series-card-original-price-font-weight-mobile;
            }
        }
        .price {
            font-family: @font_01;
            font-size: @series-card-price-font-size;
            font-weight: @series-card-price-font-weight;
            line-height: 1.2;
            letter-spacing: 0;
            color: @series-card-price-color;
            @media @snapeak_mobile {
                font-size: @series-card-price-font-size-mobile;
                font-weight: @series-card-price-font-weight-mobile;
            }

        }
    }
}

.combo-head {
    display: flex;
    align-items: center;
    font-size: @series-card-head-font-size;
    line-height: 1.2;
    letter-spacing: 0;
    font-family: @font_02;
    position: relative;
    column-gap: 12px;
    row-gap: 8px;
    flex-wrap: wrap;
    &.selectable{
        cursor: pointer;
    }
    @media @snapeak_mobile {
        flex-direction: row;
        flex-wrap: wrap;
    }
    .combo-title {
        font-weight: 500;
        color: @font_primary;
        word-break: break-word;
        @media @snapeak_mobile {
            font-size: 16px;
            line-height: 20px;
            &.selectable:active{
                color: @brand_lightest;
            }
        }

    }
    .combo-expand {
        position: absolute;
        display: flex;
        right: 0;
        top: 0;
        img {
            @media @snapeak_mobile {
                width: 20px;
                height: 20px;
            }
        }
    }

}

.combo-item {
    margin: 0 24px;
    @media @snapeak_mobile {
        margin: 0;
    }
    &:nth-of-type(n+2) {
        margin-top: 12px;
    }
    &.multi-plan{
        border-top: 0;
        &.multi-plan {
            .combo-head {
                .combo-no-desc {
                    border-bottom: 1px solid @line_light;
                }
            }

        }
    }
    .multi-plan{
        margin-top: 12px;

    }
    .combo-head {
        margin-top: 24px;
        &.combo-single:not(:last-of-type) {
            padding-bottom: 12px;
            border-bottom: 1px solid @line;
        }
    }
    &:last-of-type {
        .describe {
            &.expandable{
                padding-bottom: 0 !important;
                border-bottom: none !important;
            }
        }
    }

}

.spanRotate,.spanReset{
    color: @theme;
}

.spanRotate {
    transform: rotate(180deg);
    -webkit-transform: rotate(180deg);
    transition: transform .5s;
}

.spanReset {
    transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transition: transform .5s;
}

.combo-disabled{
    color: @disable !important;
    cursor: not-allowed;
    pointer-events: none;
    &:hover{
        color: @disable !important;
        cursor: not-allowed;
    }
    div,p,span{
        color: @disable !important;
    }
    /deep/ svg{
        color: @disable !important;
    }
}
</style>
